import { Badge, Button, Group, Loader, Paper, Progress, Text, Tooltip } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'

import { formatDateTime } from '@/utils'
import { ArtifactsView } from '@/pages/Assistant/components/ArtifactsView'
import type { UsePatientClinicalSummaryOverviewResult } from '@/hooks/usePatientClinicalSummaryOverview'
import { ClinicalSummaryLimitations } from './ClinicalSummaryLimitations'

// Shown centered while the first-ever overview is being generated, until the
// agent's live thinking feed (beingSupersededThinking) starts arriving.
const OVERVIEW_GENERATING_DEFAULT = 'Clinical Summary Overview is being generated'

// The hook lives in the ClinicalSummary container (the status controls render
// in the shared header row, outside this tab panel), so both components
// receive its result as a prop.
type Props = {
  overview: UsePatientClinicalSummaryOverviewResult
}

// Compact elapsed time since the rebuild started, e.g. "42s" / "3m 05s". The
// 2s being-superseded polling re-renders often enough to keep it ticking.
const elapsedSince = (iso: string): string => {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000))
  if (!Number.isFinite(seconds)) return ''
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`
}

// Rebuild progress (or staleness) + the rebuild trigger. Rendered by the page
// header on the Overview tab; empty until the first read lands.
export const ClinicalSummaryOverviewStatus = ({ overview }: Props) => {
  const { data, refreshing, requestRefresh } = overview
  if (!data) return null

  // A rebuild of an overview that's still on screen carries its live feedback in
  // the header; a first-ever build has nothing to show there yet, so the body's
  // centered indicator owns the explanation and the header stays minimal (just
  // the elapsed timer).
  const isSubsequentRefresh = data.beingSuperseded && !!data.clinicalSummaryOverview
  const elapsed = data.beingSupersededSince ? elapsedSince(data.beingSupersededSince) : '…'

  return (
    <Group justify="flex-end" align="center" gap="sm" wrap="nowrap">
      {/* Right-aligned status slot to the left of Refresh. While building, the
          spinner + elapsed timer always sit here (next to the button); a refresh
          of an existing overview additionally shows its live thinking feed
          (100px, ellipsized, full text on hover) to their left. When not
          building, a single staleness/verifying badge. */}
      <Group justify="flex-end" align="center" gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
        {data.beingSuperseded ? (
          <>
            {isSubsequentRefresh && (
              <Tooltip
                label={data.beingSupersededThinking}
                disabled={!data.beingSupersededThinking}
                multiline
                w={320}
                withArrow
              >
                <Text size="sm" c="dimmed" ta="right" truncate style={{ width: 100 }}>
                  {data.beingSupersededThinking ?? ''}
                </Text>
              </Tooltip>
            )}
            <Loader size="xs" />
            <Text size="sm" c="dimmed" className="whitespace-nowrap">
              {elapsed}
            </Text>
          </>
        ) : (
          <>
            {data.lastBuildFailed ? (
              // The last rebuild failed: shown IN PLACE of the staleness badge.
              // Automatic rebuilds are stopped server-side; the Refresh button retries.
              <Tooltip
                label={`${data.lastBuildFailed.reason} — ${formatDateTime(data.lastBuildFailed.at, '/')}`}
                withArrow
              >
                <Badge color="red" variant="light">
                  Failed to refresh
                </Badge>
              </Tooltip>
            ) : data.upToDate === undefined ? (
              // The fast read skips the staleness check; the follow-up verifying
              // call fills upToDate in.
              <Badge color="blue" variant="light">
                Verifying
              </Badge>
            ) : (
              <Badge color={data.upToDate ? 'green' : 'yellow'} variant="light">
                {data.upToDate ? 'Up to date' : 'Out of date'}
              </Badge>
            )}
          </>
        )}
      </Group>
      <Button
        size="sm"
        variant="default"
        onClick={requestRefresh}
        loading={refreshing}
        disabled={data.beingSuperseded}
        leftSection={<IconRefresh size={14} />}
      >
        Refresh
      </Button>
    </Group>
  )
}

// The "Generated: …" stamp of the stored overview — its own header part (separate from the status
// controls) so the page header can space and wrap the two independently.
export const ClinicalSummaryOverviewGeneratedAt = ({ overview }: Props) => {
  const generatedAt = overview.data?.clinicalSummaryOverview?.generatedAt
  if (!generatedAt) return null
  return (
    <Text size="sm" c="dimmed" className="whitespace-nowrap">
      Generated: {formatDateTime(generatedAt, '/')}
    </Text>
  )
}

// The stored AI-generated overview (narrative + charts/tables/kpis) rendered
// through the assistant's shared artifact renderer. Suggestions are hidden —
// this is a read-only surface with no chat to follow up in.
export const ClinicalSummaryOverview = ({ overview }: Props) => {
  const { data, loading, error, refresh } = overview

  if (loading && !data) {
    return <p className="py-12 text-center text-[#7D7987]">Loading overview...</p>
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="mb-4 text-red-600">{error}</p>
        <Button variant="default" onClick={refresh} leftSection={<IconRefresh size={14} />}>
          Retry
        </Button>
      </div>
    )
  }

  if (!data) return null

  const stored = data.clinicalSummaryOverview
  if (!stored) {
    // First-ever build: the panel would otherwise sit blank for ~2 minutes, so
    // show a centered progress bar + the agent's live thinking. The spinner
    // lives in the header (next to the timer/button), not here.
    if (data.beingSuperseded) {
      return (
        <div className="flex flex-col items-center gap-4 py-12 text-center">
          <Progress value={100} animated className="w-full max-w-xs" />
          <Text size="sm" c="dimmed" className="line-clamp-4 max-w-xl">
            {data.beingSupersededThinking ?? OVERVIEW_GENERATING_DEFAULT}
          </Text>
        </div>
      )
    }
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed">
          No overview has been generated for this patient yet.
        </Text>
      </Paper>
    )
  }

  return (
    // ArtifactsView returns a fragment; the caller supplies the flex column.
    // The "Generated:" timestamp lives in the header status bar.
    <div className="flex flex-col gap-3">
      {/* Same data-completeness banner as the Details tab, driven by the meta of the details this
          overview was built from. The banner self-hides when there are no limitations; detailsMeta
          is absent on overviews stored before the meta was recorded. */}
      {stored.detailsMeta && <ClinicalSummaryLimitations meta={stored.detailsMeta} />}
      <ArtifactsView artifacts={stored.artifacts} showSuggestions={false} />
    </div>
  )
}
